import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable, of } from "rxjs";
import { map, pluck } from "rxjs/operators";
import {
  GET_US_CALCULATOR_FIELD_ITEMS,
  GET_US_CALCULATOR_PRODUCTS,
  GET_CALCULATION_INPUT_BY_SUBCATEGORY,
  GET_PAGE_VALUES_FOR_PRODUCT,
  GET_CALCULATION_INPUT_BY_MULTIPLE_SUBCATEGORY,
  GET_DEPENDANT_FIELD_ITEMS,
  CREATE_CALCULATION_INPUT,
  DELETE_CALCULATION_INPUT,
  GET_CALCULATION_INPUTS_BY_PUBLISHER,
} from "../graphql/calculatorUsGql";
import {
  UsCalculatorFieldItem,
  UsCalculatorFieldItemExport,
} from "../model/calculator-us-remastered/usCalculatorFieldItem";

export interface UsCalculatorFieldItemsQuery {
  publisherId: string;
  classification: string;
  subcategory: string;
  inputName: string;
}

@Injectable({
  providedIn: "root",
})
export class CalculatorUsFieldItemsService {
  constructor(private apollo: Apollo) {}

  /**
   * Retrieves US Calculator Field Items based on dynamic parameters
   * @param params Query parameters for fetching field items
   * @returns Observable of UsCalculatorFieldItem array
   */
  getUsCalculatorFieldItems(
    params: UsCalculatorFieldItemsQuery
  ): Observable<UsCalculatorFieldItem[]> {
    return this.apollo
      .query<{ getUsCalculatorFieldItems: UsCalculatorFieldItem[] }>({
        query: GET_US_CALCULATOR_FIELD_ITEMS,
        variables: {
          publisherId: params.publisherId,
          classification: params.classification,
          subcategory: params.subcategory,
          inputName: params.inputName,
        },
        fetchPolicy: "no-cache",
      })
      .pipe(map((result) => result.data.getUsCalculatorFieldItems));
  }

  /**
   * Convenience method for getting field items for products (initial load)
   * Gets all products available for a publisher
   */
  getProductFieldItems(
    publisherId: string
  ): Observable<UsCalculatorFieldItem[]> {
    return this.getUsCalculatorProducts(<any>{
      publisherId,
    });
  }

  getUsCalculatorProducts(
    params: UsCalculatorFieldItemsQuery
  ): Observable<UsCalculatorFieldItem[]> {
    return this.apollo
      .query<{ getUsCalculatorProducts: UsCalculatorFieldItem[] }>({
        query: GET_US_CALCULATOR_PRODUCTS,
        variables: {
          publisherId: params.publisherId,
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((result) => result.data.getUsCalculatorProducts));
  }

  /**
   * Get pages
   */
  getPageValuesForProductType(
    publisherId: string,
    classification: string
  ): Observable<UsCalculatorFieldItem[]> {
    return this.apollo
      .query<{ getPageValuesForProductType: UsCalculatorFieldItem[] }>({
        query: GET_PAGE_VALUES_FOR_PRODUCT,
        variables: {
          publisherId: publisherId,
          classification: classification,
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((result) => result.data.getPageValuesForProductType));
  }

  /**
   * Case Type-dependent field items that return different defaults based on selected Case Type
   * This simulates the backend dependsOnField/dependsOnValue functionality
   */
  getDependantFieldItems(
    publisherId: string,
    classification: string,
    subcategory: string,
    dependsOnField: string,
    dependsOnValue: string
  ): Observable<UsCalculatorFieldItem[]> {
    // For real GraphQL calls, we would pass the selectedCaseType as a parameter
    return this.apollo
      .query<{ getDependantFieldItems: UsCalculatorFieldItem[] }>({
        query: GET_DEPENDANT_FIELD_ITEMS,
        variables: {
          publisherId: publisherId,
          classification: classification,
          subcategory: subcategory,
          dependsOnField: dependsOnField,
          dependsOnValue: dependsOnValue,
        },
        fetchPolicy: "cache-first",
      })
      .pipe(pluck("data", "getDependantFieldItems"));
  }

  /**
   * Lädt alle Field Items für eine Subkategorie auf einmal (neue optimierte Methode)
   */
  public getFieldItemsBySubcategory(
    publisherId: string,
    classification: string,
    subcategory: string | string[]
  ): Observable<UsCalculatorFieldItem[]> {
    if (typeof subcategory === "string") {
      // Echte GraphQL-Query für Subkategorie
      return this.apollo
        .query({
          query: GET_CALCULATION_INPUT_BY_SUBCATEGORY,
          variables: {
            publisherId,
            classification,
            subcategory,
          },
          fetchPolicy: "no-cache",
        })
        .pipe(pluck("data", "calculationInputsBySubcategory"));
    } else {
      // Echte GraphQL-Query für Subkategorie
      return this.apollo
        .query({
          query: GET_CALCULATION_INPUT_BY_MULTIPLE_SUBCATEGORY,
          variables: {
            publisherId,
            classification,
            subcategory,
          },
          fetchPolicy: "no-cache",
        })
        .pipe(pluck("data", "calculationInputsByMultipleSubcategory"));
    }
  }

  getCalculationInputsByPublisher(
    publisherId: string
  ): Observable<UsCalculatorFieldItemExport[]> {
    return this.apollo
      .query({
        query: GET_CALCULATION_INPUTS_BY_PUBLISHER,
        variables: {
          publisherId,
        },
        fetchPolicy: "no-cache",
      })
      .pipe(pluck("data", "calculationInputsByPublisher"));
  }

  createCalculationInput(
    input: UsCalculatorFieldItemExport
  ): Observable<UsCalculatorFieldItemExport> {
    return this.apollo
      .mutate({
        mutation: CREATE_CALCULATION_INPUT,
        variables: {
          input: input,
        },
      })
      .pipe(pluck("data", "createCalculationInput"));
  }

  deleteCalculationInput(id: string): Observable<boolean> {
    return this.apollo
      .mutate({
        mutation: DELETE_CALCULATION_INPUT,
        variables: { id: id },
      })
      .pipe(pluck("data", "deleteCalculationInput"));
  }

  public getDbClassification(classification: string) {
    if (!classification) return "";

    // Map product classifications to DB classifications
    if (classification.includes("HARDCOVER")) {
      return "HARDCOVER";
    } else if (classification.includes("SOFTCOVER")) {
      return "SOFTCOVER";
    } else if (classification.includes("COMPONENT")) {
      return "COMPONENTS";
    }

    // Fallback: return original if no mapping found
    return classification;
  }
}
